import "~/styles/global.css?__remix_sideEffect__";

import { useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import FingerprintSDK from '@cx-sdk/fp';
import { type MyOCData } from '@cx-web/domain-types/myoc';
import { useLoaderData } from '@remix-run/react';
import { withSentry } from '@sentry/remix';

import { PlusConfigProvider } from '~/apps/product-plus/providers/plus-config-context';
import { BetslipProvider } from '~/providers/betslip-context';
import { FeatureFlagProvider, featureFlags } from '~/providers/feature-flag-provider';
import { MyOCUserProvider } from '~/providers/myoc-user';
import { OddsFormatProvider } from '~/providers/odds-format-context';
import { RepubProvider } from '~/providers/repub-context';
import { ServicesProvider } from '~/providers/services-context';
import { getClientI18n } from '~/utils/getClientI18n';

import Body from './body';
import Head from './head';
import { type loader } from './root-loader';

export { loader } from './root-loader';
export { RootErrorBoundary as ErrorBoundary } from './root-error-boundary';

function App() {
  const {
    ENV,
    featureToggleDefinitions,
    geolocationInfo,
    labels,
    lng,
    myocData,
    oddsFormat,
    persistedBetslipData,
    repub,
    resources,
    siteConfig
  } = useLoaderData<typeof loader>();

  const i18n = getClientI18n({ lng, resources });

  useEffect(() => {
    const fingerprintingEnabled = featureToggleDefinitions.find(
      (def) => def.name === featureFlags.SUBS__FINGERPRINTING
    )?.enabled;
    if (!fingerprintingEnabled) {
      return;
    }

    async function fingerprintClient() {
      if (localStorage && !localStorage.getItem('fpsdk-set')) {
        try {
          const fingerprintSDK = await FingerprintSDK.load();
          const result = await fingerprintSDK.get();
          const response = await fetch('/api/cx/fp', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(result)
          });
          const payload = (await response.json()) as {visitorId: string;};
          if (payload.visitorId) {
            localStorage.setItem('fpsdk-set', payload.visitorId);
          }
        } catch (error) {}
      }
    }
    fingerprintClient();
  }, [featureToggleDefinitions]);

  return (
    <I18nextProvider i18n={i18n}>
      <html lang={lng}>
        <FeatureFlagProvider featureToggleDefinitions={featureToggleDefinitions}>
          <RepubProvider repub={repub}>
            <PlusConfigProvider>
              <MyOCUserProvider myocData={myocData as MyOCData}>
                <ServicesProvider
                  NODE_ENV={ENV.NODE_ENV}
                  geolocationInfo={geolocationInfo}
                  labels={labels}
                  repub={repub}
                  siteConfig={siteConfig}>

                  <OddsFormatProvider userOddsFormat={oddsFormat}>
                    <BetslipProvider persistedBetslipData={persistedBetslipData} userOddsFormat={oddsFormat}>
                      <Head />
                      <Body />
                    </BetslipProvider>
                  </OddsFormatProvider>
                </ServicesProvider>
              </MyOCUserProvider>
            </PlusConfigProvider>
          </RepubProvider>
        </FeatureFlagProvider>
      </html>
    </I18nextProvider>);

}

export default withSentry(App);